import axios from 'axios';

/**
 * Shared axios instance — see docs/jtctm-fable5-suggest/08-ui-common-controls.md §2.
 *
 * Most pages still build their own `const API = process.env.REACT_APP_BACKEND_URL...`
 * and call bare `axios`; this is the one instance new/touched code should import
 * instead. baseURL already includes `/api`, so callers pass paths like `api.get('/leads')`.
 *
 * Response interceptor:
 *   - 401 → redirect to /login, mirroring what AuthContext + ProtectedRoute already do
 *     for a logged-out session — except we skip the bootstrap `/auth/me` check (a 401
 *     there is the expected "not logged in yet" case AuthContext already handles by
 *     setting user=null; forcing a redirect there would break public pages like
 *     /reset-password that call it before the user is authenticated) and skip when
 *     already on a public route (no-op redirect / avoids loops).
 *   - No generic error toast here: callers keep their own catch + toast (the app-wide
 *     pattern), so an interceptor toast would double-fire on every 5xx. Use getErrMsg()
 *     in the caller's catch instead.
 */

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/setup'];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';

    if (status === 401) {
      const isBootstrapCheck = url.includes('/auth/me');
      const onPublicPath = typeof window !== 'undefined' && PUBLIC_PATHS.includes(window.location.pathname);
      if (!isBootstrapCheck && !onPublicPath && typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Extract a human-readable message from an axios/fetch error, mirroring the
 * `e.response?.data?.detail || e.message || fallback` shape repeated inline
 * across the app (FastAPI returns `{ detail: ... }` on errors).
 */
export function getErrMsg(e, fallback = 'Something went wrong') {
  return e?.response?.data?.detail || e?.message || fallback;
}

export default api;
